import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Collapse, IconButton, Paper, Switch, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { TFunction, useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Swal from 'sweetalert2';

import { SCUICustomDataGrid } from '../../atomic/molecules';
import { SCUIButton } from '../../atomic/atoms';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import {
    OptHistoryGridRow,
    StoreCustomerManagementCustomer,
} from '../../services/query/storeCustomerManagement/StoreCustomerManagementQuery.services';
import { StoreCustomerManagementCommand } from '../../services/command/storeCustomerManagement/StoreCustomerManagementCommand.services';
import CustomerCommand from '../../services/command/customer/CustomerCommand.services';
import TimeHelper from '../../utils/time';
import { useStoreCustomerShell } from './StoreCustomerShell';

enum Source {
    None = 0,
    Web = 1,
    IOS = 2,
    Android = 3,
    Backoffice = 4,
    EmailLink = 5,
    IYS = 6,
    CRM = 7,
    StoreSMS = 8,
    IVR = 9,
    Insider = 10,
}

const ECOMMERCE_CUSTOMER_TYPES: ReadonlyArray<string> = ['OuterCustomer', 'Personel'];

const getSelectedBackofficeCountryCode = (): string => {
    try {
        const raw = localStorage.getItem('selectedCountry');
        if (!raw) return '';
        return JSON.parse(raw)?.countryCode ?? '';
    } catch {
        return '';
    }
};

type SelectedCountryRow = {
    countryCode: string;
    firstName: string;
    lastName: string;
    phone: string;
    isSmsOptin: boolean;
    isCallOptin: boolean;
    platform: number;
    source: number;
    customerType: any;
};

const buildSelectedCountryRow = (
    customer: StoreCustomerManagementCustomer | null,
    selectedCountryCode: string
): SelectedCountryRow | null => {
    if (!customer || !selectedCountryCode) return null;

    const detail = customer.customerDetails?.find((d) => d.countryCode === selectedCountryCode);
    const phone = customer.customerPhones?.find((p) => p.countryCode === selectedCountryCode);
    const pref = customer.preferences?.find((p) => p.countryCode === selectedCountryCode);
    if (!detail && !phone && !pref) {
        return null;
    }

    return {
        countryCode: selectedCountryCode,
        firstName: detail?.firstName ?? '',
        lastName: detail?.lastName ?? '',
        phone: phone?.phone ?? '',
        isSmsOptin: pref?.isSmsOptin ?? false,
        isCallOptin: pref?.isCallOptin ?? false,
        platform: customer.platform,
        source: Source.Backoffice,
        customerType: customer.customerType,
    };
};

const mapOptHistoryItem = (item: any, index: number, t: TFunction): OptHistoryGridRow => ({
    id: index,
    date: TimeHelper.formatDateHour(item.historyDate) || '-',
    platform: item.sourceName || '-',
    source: item.platformName || '',
    status: item.isSmsOptin === true ? t('general.active') : t('general.passive') || '',
    permissionValue: item.consentPhone || '-',
    ipAddress: item.ipAddress || '-',
    device: item.userAgent || '-',
});

type PermissionRowProps = {
    title: string;
    optin: boolean;
    expanded: boolean;
    historyRows: OptHistoryGridRow[];
    historyColumns: { field: string; headerName: string; flex: number }[];
    historyLoading: boolean;
    onToggle: () => void;
    onExpand: () => void;
    activeLabel: string;
    passiveLabel: string;
    currentStatusLabel: string;
};

const PermissionRow = memo(function PermissionRow(props: PermissionRowProps) {
    const {
        title,
        optin,
        expanded,
        historyRows,
        historyColumns,
        historyLoading,
        onToggle,
        onExpand,
        activeLabel,
        passiveLabel,
        currentStatusLabel,
    } = props;

    return (
        <Box>
            <Box
                role="button"
                tabIndex={0}
                onClick={onExpand}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onExpand();
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 2,
                    mb: 1,
                    cursor: 'pointer',
                }}
            >
                <Box
                    sx={{
                        width: 6,
                        alignSelf: 'stretch',
                        borderRadius: 1,
                        backgroundColor: optin ? '#4caf50' : '#f44336',
                        mr: 2,
                        flexShrink: 0,
                    }}
                />
                <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={600}>{title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {currentStatusLabel}{' '}
                        <span style={{ color: optin ? '#4caf50' : '#f44336', fontWeight: 600 }}>
                            {optin ? activeLabel : passiveLabel}
                        </span>
                    </Typography>
                </Box>
                <Box onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <Switch checked={optin} onChange={onToggle} />
                </Box>
                <IconButton
                    tabIndex={-1}
                    aria-hidden
                    sx={{
                        pointerEvents: 'none',
                        transition: 'transform 0.2s',
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                >
                    <ExpandMoreIcon />
                </IconButton>
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ px: 2, pb: 2 }}>
                    <SCUICustomDataGrid
                        rows={historyRows}
                        columns={historyColumns}
                        pageSize={5}
                        loading={historyLoading}
                        style={{ marginTop: 0 }}
                    />
                </Box>
            </Collapse>
        </Box>
    );
});

export const StoreCustomerPermissionsPanel: FC = () => {
    const { t } = useTranslation();
    const { globalCustomer, refreshMemberDatas } = useStoreCustomerShell();
    const [customer, setCustomer] = useState<StoreCustomerManagementCustomer | null>(globalCustomer);

    const selectedCountryCode = useMemo(() => getSelectedBackofficeCountryCode(), []);

    const [smsOptinOverride, setSmsOptinOverride] = useState<boolean | null>(null);
    const [callOptinOverride, setCallOptinOverride] = useState<boolean | null>(null);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [smsExpanded, setSmsExpanded] = useState(false);
    const [callExpanded, setCallExpanded] = useState(false);
    const [smsHistoryRows, setSmsHistoryRows] = useState<OptHistoryGridRow[]>([]);
    const [callHistoryRows, setCallHistoryRows] = useState<OptHistoryGridRow[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);

    useEffect(() => {
        setCustomer(globalCustomer);
        setSmsOptinOverride(null);
        setCallOptinOverride(null);
        setHistoryLoaded(false);
        setSmsExpanded(false);
        setCallExpanded(false);
    }, [globalCustomer]);

    const selectedRow = useMemo(
        () => buildSelectedCountryRow(customer, selectedCountryCode),
        [customer, selectedCountryCode]
    );

    const isEcommerce = customer != null && ECOMMERCE_CUSTOMER_TYPES.includes(customer.customerType ?? '');
    const isStoreCustomer = customer != null && !isEcommerce;
    const smsOptin = smsOptinOverride !== null ? smsOptinOverride : (selectedRow?.isSmsOptin ?? false);
    const callOptin = callOptinOverride !== null ? callOptinOverride : (selectedRow?.isCallOptin ?? false);

    const loadOptHistory = useCallback(async () => {
        if (!customer || historyLoaded || historyLoading) return;
        setHistoryLoading(true);
        try {
            const res: any = await CustomerCommand.GetOptHistoryListV2({ customerId: customer.ssoCustomerId });
            const data = res?.data ?? {};
            setSmsHistoryRows(
                Array.isArray(data.smsHistoryList)
                    ? data.smsHistoryList.map((item: any, index: number) => mapOptHistoryItem(item, index, t))
                    : []
            );
            setCallHistoryRows(
                Array.isArray(data.callHistoryList)
                    ? data.callHistoryList.map((item: any, index: number) => mapOptHistoryItem(item, index, t))
                    : []
            );
            setHistoryLoaded(true);
        } catch {
            Swal.fire(t('general.error'), t('general.anErrorOccured'), 'error');
        } finally {
            setHistoryLoading(false);
        }
    }, [customer, historyLoaded, historyLoading, t]);

    const handleUpdate = useCallback(async () => {
        if (!selectedRow) return;
        setUpdateLoading(true);
        try {
            await StoreCustomerManagementCommand.updateGlobalCustomerByPhone({
                phone: selectedRow.phone,
                platform: selectedRow.platform,
                source: Source.Backoffice,
                updatableData: {
                    countryCode: selectedRow.countryCode,
                    isSmsOptin: smsOptin,
                    isCallOptin: callOptin,
                },
                SsoRequirement: {
                    customerType: selectedRow.customerType,
                },
            });
            Swal.fire({ icon: 'success', timer: 1200, showConfirmButton: false, text: t('general.success') });
            setCustomer((prev) =>
                prev
                    ? {
                          ...prev,
                          preferences: prev.preferences.map((p) =>
                              p.countryCode === selectedRow.countryCode
                                  ? { ...p, isSmsOptin: smsOptin, isCallOptin: callOptin }
                                  : p
                          ),
                      }
                    : prev
            );
            setSmsOptinOverride(null);
            setCallOptinOverride(null);
            setHistoryLoaded(false);
            refreshMemberDatas();
            if (smsExpanded || callExpanded) {
                void loadOptHistory();
            }
        } catch {
            Swal.fire(t('general.error'), t('general.anErrorOccured'), 'error');
        } finally {
            setUpdateLoading(false);
        }
    }, [selectedRow, smsOptin, callOptin, smsExpanded, callExpanded, loadOptHistory, refreshMemberDatas, t]);

    const historyColumns = useMemo(
        () => [
            { field: 'date', headerName: t('storeCustomerManagement.historyColumns.date'), flex: 1 },
            { field: 'source', headerName: t('storeCustomerManagement.historyColumns.source'), flex: 1 },
            { field: 'platform', headerName: t('storeCustomerManagement.historyColumns.platform'), flex: 1 },
            { field: 'status', headerName: t('storeCustomerManagement.historyColumns.status'), flex: 1 },
            {
                field: 'permissionValue',
                headerName: t('storeCustomerManagement.historyColumns.permissionValue'),
                flex: 1,
            },
            { field: 'ipAddress', headerName: t('storeCustomerManagement.historyColumns.ipAddress'), flex: 1 },
            { field: 'device', headerName: t('storeCustomerManagement.historyColumns.device'), flex: 1 },
        ],
        [t]
    );

    if (!customer) {
        return null;
    }

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            {isEcommerce && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffe082',
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                    }}
                >
                    <WarningAmberIcon sx={{ color: '#f9a825', mt: '2px' }} />
                    <Box>
                        <Typography fontWeight={600} sx={{ color: '#e65100' }}>
                            {t('storeCustomerManagement.ecommerceWarningTitle')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6d4c00' }}>
                            SMS izin güncelleme işlemini{' '}
                            <RouterLink
                                to={`${PageUrl.customerManagement.url}?ActiveTab=CustomerInformation&customerId=${customer.ssoCustomerId}`}
                                style={{ color: '#1976d2', fontWeight: 600, textDecoration: 'underline' }}
                            >
                                Üye Bilgileri Yönetimi
                            </RouterLink>{' '}
                            ekranından gerçekleştirebilirsiniz.
                        </Typography>
                    </Box>
                </Box>
            )}

            {isStoreCustomer && !selectedRow && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffe082',
                        borderRadius: 1,
                        p: 2,
                    }}
                >
                    <WarningAmberIcon sx={{ color: '#f9a825', mt: '2px' }} />
                    <Typography variant="body2" sx={{ color: '#6d4c00' }}>
                        {t('storeCustomerManagement.noDataForSelectedCountry')}
                    </Typography>
                </Box>
            )}

            {isStoreCustomer && selectedRow && (
                <>
                    <PermissionRow
                        title={t('storeCustomerManagement.smsPermissionStatus')}
                        optin={smsOptin}
                        expanded={smsExpanded}
                        historyRows={smsHistoryRows}
                        historyColumns={historyColumns}
                        historyLoading={historyLoading}
                        onToggle={() =>
                            setSmsOptinOverride((prev) => {
                                const current = prev !== null ? prev : (selectedRow?.isSmsOptin ?? false);
                                return !current;
                            })
                        }
                        onExpand={() => {
                            setSmsExpanded((prev) => {
                                const next = !prev;
                                if (next) void loadOptHistory();
                                return next;
                            });
                        }}
                        activeLabel={t('storeCustomerManagement.active')}
                        passiveLabel={t('storeCustomerManagement.passive')}
                        currentStatusLabel={t('storeCustomerManagement.currentStatus')}
                    />
                    <PermissionRow
                        title={t('storeCustomerManagement.callPermissionStatus')}
                        optin={callOptin}
                        expanded={callExpanded}
                        historyRows={callHistoryRows}
                        historyColumns={historyColumns}
                        historyLoading={historyLoading}
                        onToggle={() =>
                            setCallOptinOverride((prev) => {
                                const current = prev !== null ? prev : (selectedRow?.isCallOptin ?? false);
                                return !current;
                            })
                        }
                        onExpand={() => {
                            setCallExpanded((prev) => {
                                const next = !prev;
                                if (next) void loadOptHistory();
                                return next;
                            });
                        }}
                        activeLabel={t('storeCustomerManagement.active')}
                        passiveLabel={t('storeCustomerManagement.passive')}
                        currentStatusLabel={t('storeCustomerManagement.currentStatus')}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <SCUIButton
                            text={t('storeCustomerManagement.update')}
                            type="primary"
                            sizeType="default"
                            size="m"
                            onClick={handleUpdate}
                            disabled={updateLoading}
                        />
                    </Box>
                </>
            )}
        </Paper>
    );
};
